/**
 * ContextReadinessPanel.tsx
 *
 * Displays context readiness analysis for a category or product.
 * Shows overall score, dimension breakdown, recommendations with action links,
 * and research priorities.
 *
 * Created: 2025-11-29
 */

import React, { useState, useEffect } from 'react';
import { authFetch } from '@/utils/auth-fetch';
import { toast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  BookOpen,
  Sparkles,
  Target,
  BarChart3,
  RefreshCw,
  ExternalLink,
  HelpCircle,
  Loader2
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface DimensionDetails {
  score: number;
  details: Record<string, any>;
}

interface Recommendation {
  priority: string;
  dimension: string;
  type: string;
  issue: string;
  impact?: string;
  action: string;
  actionUrl: string;
  suggestedItems?: string[];
  effort?: string;
  stats?: Record<string, any>;
  aeoImpact?: string;
}

interface ResearchPriority {
  priority: string;
  type: string;
  topic: string;
  reason: string;
  suggestedResearch: string[];
  actionUrl: string;
  impact: string;
  category?: string;
  productCount?: number;
}

interface ContentReadiness {
  ready: boolean;
  requiredScore: number;
  currentScore: number;
  blockers: string[];
}

interface ContextReadinessData {
  category: string;
  categoryName: string;
  overallScore: number;
  status: 'ready' | 'mostly_ready' | 'needs_work' | 'not_ready';
  dimensions: {
    sectorFoundation: DimensionDetails;
    categoryReadiness: DimensionDetails;
    productEnrichment: DimensionDetails;
    productAnalysis: DimensionDetails;
  };
  recommendations: Recommendation[];
  researchPriorities: ResearchPriority[];
  contentReadiness: {
    questions: ContentReadiness;
    answers: ContentReadiness;
    aeoPages: ContentReadiness;
  };
  analyzedAt: string;
}

interface Props {
  siteId: string;
  categoryPath: string;
  onClose?: () => void;
  compact?: boolean;
}

const statusConfig = {
  ready: { color: 'bg-green-500', label: 'Ready', icon: CheckCircle2 },
  mostly_ready: { color: 'bg-yellow-500', label: 'Mostly Ready', icon: AlertCircle },
  needs_work: { color: 'bg-orange-500', label: 'Needs Work', icon: AlertTriangle },
  not_ready: { color: 'bg-red-500', label: 'Not Ready', icon: XCircle }
};

const priorityColors = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200'
};

const dimensionLabels = {
  sectorFoundation: { label: 'Sector Foundation', icon: Target },
  categoryReadiness: { label: 'Category Readiness', icon: BookOpen },
  productEnrichment: { label: 'Product Enrichment', icon: Sparkles },
  productAnalysis: { label: 'Product Analysis', icon: BarChart3 }
};

// Tooltip descriptions for each concept
const tooltipDescriptions = {
  // Content Readiness items
  questions: 'Questions are customer queries relevant to this category. They come from sector templates, question wheels, or AI-generated based on your products.',
  answers: 'Answers are AI-generated responses to questions, using your product data, sector knowledge, and brand voice to provide helpful information.',
  aeoPages: 'AEO Pages are Answer Engine Optimized content pages designed to rank well in AI search results and featured snippets.',

  // Dimension scores
  dimensionScores: 'Dimension Scores break down readiness into four key areas. Each dimension must meet minimum thresholds for optimal AEO content generation.',
  sectorFoundation: 'Measures if the sector template is properly configured with personas, benefits, and category research that inform content generation.',
  categoryReadiness: 'Evaluates if this specific category has research, questions, and topical coverage needed to generate authoritative content.',
  productEnrichment: 'Tracks how many products have AI-enhanced descriptions, features, and benefit mappings for richer content.',
  productAnalysis: 'Assesses depth of product attribute mapping, benefit rankings, and competitive positioning data available.'
};

export const ContextReadinessPanel: React.FC<Props> = ({
  siteId,
  categoryPath,
  onClose,
  compact = false
}) => {
  const [data, setData] = useState<ContextReadinessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['recommendations']));
  const [researchingTopics, setResearchingTopics] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadReadiness();
  }, [siteId, categoryPath]);

  const loadReadiness = async () => {
    setLoading(true);
    setError(null);

    try {
      const encodedCategory = encodeURIComponent(categoryPath);
      const response = await authFetch(
        `/api/context-readiness/category/${encodedCategory}?siteId=${siteId}`
      );

      if (!response.ok) {
        throw new Error('Failed to analyze context readiness');
      }

      const result = await response.json();
      if (result.success) {
        setData(result.data);
      } else {
        throw new Error(result.error || 'Analysis failed');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const navigateTo = (url: string) => {
    // Navigate to the action URL
    window.location.href = url;
  };

  const triggerResearch = async (priority: ResearchPriority) => {
    const topicKey = priority.topic;

    // Prevent duplicate clicks
    if (researchingTopics.has(topicKey)) return;

    setResearchingTopics(prev => new Set(prev).add(topicKey));

    try {
      const response = await authFetch('/api/context-readiness/research', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          topic: priority.topic,
          type: priority.type,
          category: priority.category || categoryPath,
          suggestedResearch: priority.suggestedResearch,
          reason: priority.reason
        })
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: 'Research Started',
          description: `Research for "${priority.topic}" has been triggered. Status: ${result.data.status}`,
        });

        // If already completed, offer to view it
        if (result.data.status === 'completed') {
          toast({
            title: 'Research Already Complete',
            description: 'This topic has already been researched. View the sector topics to see the results.',
          });
        }
      } else {
        throw new Error(result.error || 'Failed to trigger research');
      }
    } catch (err: any) {
      toast({
        title: 'Research Failed',
        description: err.message,
        variant: 'destructive'
      });
    } finally {
      // Remove from researching set after a delay (to show completion state)
      setTimeout(() => {
        setResearchingTopics(prev => {
          const newSet = new Set(prev);
          newSet.delete(topicKey);
          return newSet;
        });
      }, 2000);
    }
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="p-6">
          <div className="flex items-center justify-center space-x-2">
            <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Analyzing context readiness...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full border-red-200">
        <CardContent className="p-6">
          <div className="flex items-center space-x-2 text-red-600">
            <XCircle className="h-5 w-5" />
            <span>Error: {error}</span>
          </div>
          <Button variant="outline" size="sm" className="mt-4" onClick={loadReadiness}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const StatusIcon = statusConfig[data.status].icon;

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              Context Readiness: {data.categoryName}
            </CardTitle>
            <CardDescription>
              Analysis for content generation readiness
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={loadReadiness}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>
                <XCircle className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Overall Score */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold">{data.overallScore}/100</span>
              <Badge className={`${statusConfig[data.status].color} text-white`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig[data.status].label}
              </Badge>
            </div>
            <Progress value={data.overallScore} className="h-3" />
          </div>
        </div>

        {/* Content Readiness Badges */}
        <TooltipProvider>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.contentReadiness).map(([type, readiness]) => (
              <Tooltip key={type}>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className={`cursor-help ${readiness.ready ? 'border-green-500 text-green-700' : 'border-gray-300 text-gray-500'}`}
                  >
                    {readiness.ready ? (
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                    ) : (
                      <XCircle className="h-3 w-3 mr-1" />
                    )}
                    {type === 'aeoPages' ? 'AEO Pages' : type.charAt(0).toUpperCase() + type.slice(1)}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-sm">{tooltipDescriptions[type as keyof typeof tooltipDescriptions]}</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>

        {/* Dimension Breakdown */}
        {!compact && (
          <TooltipProvider>
            <div className="space-y-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-1 cursor-help w-fit">
                    Dimension Scores
                    <HelpCircle className="h-3 w-3" />
                  </h4>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p className="text-sm">{tooltipDescriptions.dimensionScores}</p>
                </TooltipContent>
              </Tooltip>
              {Object.entries(data.dimensions).map(([key, dim]) => {
                const config = dimensionLabels[key as keyof typeof dimensionLabels];
                const Icon = config.icon;
                return (
                  <Tooltip key={key}>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <TooltipTrigger asChild>
                          <span className="flex items-center gap-1 cursor-help">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            {config.label}
                          </span>
                        </TooltipTrigger>
                        <span className={dim.score < 50 ? 'text-orange-600 font-medium' : ''}>
                          {dim.score}%
                        </span>
                      </div>
                      <Progress value={dim.score} className="h-1.5" />
                    </div>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-sm">{tooltipDescriptions[key as keyof typeof tooltipDescriptions]}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>
        )}

        {/* Recommendations */}
        {data.recommendations.length > 0 && (
          <Collapsible
            open={expandedSections.has('recommendations')}
            onOpenChange={() => toggleSection('recommendations')}
          >
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Recommendations ({data.recommendations.length})
              </h4>
              {expandedSections.has('recommendations') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-2">
              {data.recommendations.slice(0, compact ? 3 : 10).map((rec, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${priorityColors[rec.priority as keyof typeof priorityColors] || 'bg-gray-50'} ${rec.priority === 'critical' ? 'ring-2 ring-red-400' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={rec.priority === 'critical' ? 'destructive' : 'outline'} className="text-xs">
                          {rec.priority.toUpperCase()}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{rec.type.replace(/_/g, ' ')}</span>
                        {rec.aeoImpact && (
                          <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-800">
                            AEO Impact: {rec.aeoImpact}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-medium">{rec.issue}</p>
                      {rec.impact && (
                        <p className="text-xs text-muted-foreground mt-1">{rec.impact}</p>
                      )}
                      <p className="text-sm mt-2">{rec.action}</p>

                      {/* Stats display for competitor enrichment */}
                      {rec.stats?.topUnenrichedBrands && rec.stats.topUnenrichedBrands.length > 0 && (
                        <div className="mt-2 p-2 bg-muted/50 rounded border border-amber-500/30 dark:border-amber-400/30">
                          <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">Top brands needing enrichment:</p>
                          <div className="flex flex-wrap gap-1">
                            {rec.stats.topUnenrichedBrands.slice(0, 5).map((brand: any, i: number) => (
                              <Badge key={i} variant="outline" className="text-xs border-amber-500/50 dark:border-amber-400/50 text-foreground">
                                {brand.brand}: {brand.count} products
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {rec.suggestedItems && rec.suggestedItems.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {rec.suggestedItems.slice(0, 5).map((item, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {item}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={rec.priority === 'critical' ? 'default' : 'outline'}
                      className={rec.priority === 'critical' ? 'bg-red-600 hover:bg-red-700' : ''}
                      onClick={() => navigateTo(rec.actionUrl)}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      {rec.type === 'competitor_enrichment' ? 'Enrich' : 'Go'}
                    </Button>
                  </div>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Research Priorities */}
        {data.researchPriorities && data.researchPriorities.length > 0 && (
          <Collapsible
            open={expandedSections.has('research')}
            onOpenChange={() => toggleSection('research')}
          >
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-yellow-500" />
                Research Priorities ({data.researchPriorities.length})
              </h4>
              {expandedSections.has('research') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-2">
              {data.researchPriorities.slice(0, compact ? 3 : 10).map((priority, idx) => (
                <div
                  key={idx}
                  className="p-3 rounded-lg border bg-blue-50 border-blue-200"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs bg-blue-100">
                          {priority.type.replace(/_/g, ' ')}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {priority.priority}
                        </Badge>
                      </div>
                      <p className="text-sm font-medium text-blue-900">{priority.topic}</p>
                      <p className="text-xs text-blue-700 mt-1">{priority.reason}</p>

                      {priority.suggestedResearch && priority.suggestedResearch.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs font-medium text-blue-800">Suggested questions:</p>
                          <ul className="text-xs text-blue-700 list-disc list-inside">
                            {priority.suggestedResearch.slice(0, 3).map((q, i) => (
                              <li key={i}>{q}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => triggerResearch(priority)}
                      disabled={researchingTopics.has(priority.topic)}
                    >
                      {researchingTopics.has(priority.topic) ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <BookOpen className="h-3 w-3 mr-1" />
                          Research
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Blockers for AEO */}
        {!data.contentReadiness.aeoPages.ready && data.contentReadiness.aeoPages.blockers.length > 0 && (
          <div className="p-3 rounded-lg border border-red-200 bg-red-50">
            <h5 className="text-sm font-medium text-red-800 mb-2">
              Blockers for AEO Page Generation
            </h5>
            <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
              {data.contentReadiness.aeoPages.blockers.map((blocker, idx) => (
                <li key={idx}>{blocker}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Analyzed timestamp */}
        <p className="text-xs text-muted-foreground text-right">
          Analyzed at {new Date(data.analyzedAt).toLocaleString()}
        </p>
      </CardContent>
    </Card>
  );
};

export default ContextReadinessPanel;
